import { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, Save } from "lucide-react";
import { useMerchant } from "@/lib/useMerchant";
import DashboardHeader from "@/components/DashboardHeader";
import DashboardFooter from "@/components/DashboardFooter";
import PublicStorefrontCard from "@/components/PublicStorefrontCard";
import BackToDashboard from "@/components/BackToDashboard";
import RichTextEditor from "@/components/RichTextEditor";
import api from "@/lib/api";

const FAQ_SLOTS = 6;
const emptyFaqs = () => Array.from({ length: FAQ_SLOTS }, () => ({ question: "", answer: "" }));

/**
 * PublicCatalogSettings.jsx
 * ------------------------------------------------------------------
 * Dashboard page for everything related to the shareable public
 * catalog page: the on/off toggle + share link (PublicStorefrontCard),
 * plus a per-product editor for the richer, optional page-only fields
 * (extended description, sizing guide, care instructions, delivery
 * info, FAQs). Deliberately kept OUT of the Catalog page, which stays
 * focused on the fields merchants touch daily (price/stock/photos).
 *
 * The product editor only appears once the public page is turned on —
 * these fields are never shown anywhere else, so there's no reason to
 * ask for them before that.
 *
 * Saves go through the same PUT /catalog endpoint Catalog.jsx uses.
 * IMPORTANT: that endpoint's `quantity` column is NOT null-safe on
 * partial updates, so every save here re-sends the product's full,
 * already-known field set alongside the 5 fields this page actually
 * edits — never a partial payload.
 * ------------------------------------------------------------------
 */
function ProductDetailsRow({ product, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(() => {
    const faqs = emptyFaqs();
    (product.faqs || []).slice(0, FAQ_SLOTS).forEach((row, i) => {
      faqs[i] = { question: row.question || "", answer: row.answer || "" };
    });
    return {
      extended_description: product.extended_description || "",
      sizing_guide: product.sizing_guide || "",
      care_instructions: product.care_instructions || "",
      delivery_info: product.delivery_info || "",
      faqs,
    };
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  const setFaq = (i, field, value) =>
    setForm((f) => {
      const next = [...f.faqs];
      next[i] = { ...next[i], [field]: value };
      return { ...f, faqs: next };
    });

  const filledFaqCount = form.faqs.filter((f) => f.question.trim() && f.answer.trim()).length;

  const save = async () => {
    setSaving(true);
    setSaved(false);
    setError(null);
    const fd = new FormData();
    fd.append("id", product.id);
    // Every other field is re-sent exactly as already stored — this form
    // never touches them, but the backend isn't null-safe on a partial save.
    fd.append("name", product.title || "");
    fd.append("price", String(product.price ?? 0));
    fd.append("quantity", product.qty === null || product.qty === undefined ? "" : String(product.qty));
    fd.append("weight_grams", product.weight_grams ?? "");
    fd.append("length_cm", product.length_cm ?? "");
    fd.append("width_cm", product.width_cm ?? "");
    fd.append("height_cm", product.height_cm ?? "");
    fd.append("stock", JSON.stringify(product.stock || []));
    fd.append("description", product.desc || "");
    fd.append("sizes", (product.sizes || []).join(", "));
    fd.append("is_digital", product.is_digital ? "true" : "false");
    fd.append("digital_delivery_url", product.digital_delivery_url || "");
    fd.append("category", product.category || "");
    fd.append("gender", product.gender || "");
    fd.append("color", product.color || "");
    fd.append("is_bestseller", product.is_bestseller ? "true" : "false");
    fd.append("preview_url", product.preview_url || "");
    fd.append("external_url", product.external_url || "");
    fd.append("external_label", product.external_label || "");
    (product.additional_image_urls || []).forEach((url, i) => {
      fd.append(`additional_image_${i + 1}_existing_url`, url);
    });
    // The 5 fields this page actually edits.
    fd.append("extended_description", form.extended_description.slice(0, 2000));
    fd.append("sizing_guide", form.sizing_guide.slice(0, 1500));
    fd.append("care_instructions", form.care_instructions.slice(0, 1000));
    fd.append("delivery_info", form.delivery_info.slice(0, 1000));
    form.faqs.forEach((row, i) => {
      fd.append(`faq${i + 1}_question`, (row.question || "").slice(0, 80));
      fd.append(`faq${i + 1}_answer`, (row.answer || "").slice(0, 400));
    });

    try {
      const { data } = await api.put("/catalog", fd);
      onSaved(data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      setError("Couldn't save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl border border-ink/10 bg-white">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
      >
        <div className="flex min-w-0 items-center gap-3">
          {product.image && (
            <img src={product.image} alt="" className="h-10 w-10 flex-none rounded-lg object-cover" />
          )}
          <div className="min-w-0">
            <span className="block truncate text-sm font-semibold text-ink">{product.title}</span>
            {(product.extended_description || filledFaqCount > 0 || product.sizing_guide || product.care_instructions || product.delivery_info) && (
              <span className="text-xs text-mint-dark">Details added</span>
            )}
          </div>
        </div>
        {open ? <ChevronUp size={16} className="flex-none text-ink/40" /> : <ChevronDown size={16} className="flex-none text-ink/40" />}
      </button>
      {open && (
        <div className="space-y-4 border-t border-ink/10 p-4">
          {error && <p className="text-sm text-hotpink">{error}</p>}
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-ink/50">
              Extended description <span className="normal-case text-ink/30">· {form.extended_description.length}/2000</span>
            </label>
            <RichTextEditor
              rows={4}
              maxLength={2000}
              value={form.extended_description}
              onChange={(v) => setForm((f) => ({ ...f, extended_description: v }))}
              placeholder="The longer story — materials, fit notes, what makes it worth buying."
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-ink/50">Sizing guide</label>
            <RichTextEditor
              rows={3}
              maxLength={1500}
              value={form.sizing_guide}
              onChange={(v) => setForm((f) => ({ ...f, sizing_guide: v }))}
              placeholder="Chest 38in = M, 40in = L…"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-ink/50">Care / washing instructions</label>
            <RichTextEditor
              rows={3}
              maxLength={1000}
              value={form.care_instructions}
              onChange={(v) => setForm((f) => ({ ...f, care_instructions: v }))}
              placeholder="Machine wash cold, hang dry…"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-ink/50">Delivery info</label>
            <RichTextEditor
              rows={3}
              maxLength={1000}
              value={form.delivery_info}
              onChange={(v) => setForm((f) => ({ ...f, delivery_info: v }))}
              placeholder="Ships in 2-3 days, delivery in 5-7 days…"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-ink/50">
              Product FAQs <span className="normal-case text-ink/30">· up to {FAQ_SLOTS}</span>
            </label>
            <div className="space-y-2">
              {form.faqs.map((row, i) => (
                <div key={i} className="rounded-xl border border-ink/10 p-2.5">
                  <input
                    value={row.question}
                    onChange={(e) => setFaq(i, "question", e.target.value.slice(0, 80))}
                    placeholder={`Question ${i + 1}`}
                    className="mb-1.5 w-full rounded-lg border border-ink/15 px-2.5 py-1.5 text-sm focus:border-hotpink focus:outline-none"
                  />
                  <input
                    value={row.answer}
                    onChange={(e) => setFaq(i, "answer", e.target.value.slice(0, 400))}
                    placeholder="Answer"
                    className="w-full rounded-lg border border-ink/15 px-2.5 py-1.5 text-sm focus:border-hotpink focus:outline-none"
                  />
                </div>
              ))}
            </div>
          </div>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-1.5 rounded-full bg-ink px-4 py-2 text-sm font-semibold text-cream hover:bg-ink/85 disabled:opacity-50"
          >
            <Save size={14} /> {saving ? "Saving…" : saved ? "Saved!" : "Save"}
          </button>
        </div>
      )}
    </div>
  );
}

export default function PublicCatalogSettings() {
  const { me, loading } = useMerchant();
  const [enabled, setEnabled] = useState(null); // null = not known yet
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    setLoadingProducts(true);
    api
      .get("/catalog")
      .then(({ data }) => setProducts(data.items || []))
      .finally(() => setLoadingProducts(false));
  }, [enabled]);

  const handleSaved = (saved) => {
    setProducts((prev) => prev.map((p) => (p.id === saved.id ? { ...p, ...saved } : p)));
  };

  if (loading || !me) {
    return (
      <div className="min-h-screen bg-cream">
        <DashboardHeader me={me} />
        <div className="grid h-64 place-items-center text-sm text-ink/50">Loading…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <DashboardHeader me={me} />
      <section className="mx-auto max-w-3xl px-6 py-10 md:px-14">
        <BackToDashboard />
        <h1 className="text-2xl font-black text-ink">Public catalog page</h1>
        <p className="mt-1 text-sm text-ink/60">
          Turn on your shareable storefront, then optionally add extra details to any product's own page.
        </p>

        <PublicStorefrontCard plan={me.plan} onEnabledChange={setEnabled} />

        {me.plan !== "free" && enabled === false && (
          <div className="mt-8 rounded-3xl border border-ink/10 bg-white p-6 text-sm text-ink/60">
            Turn on your public catalog page above to start adding extended descriptions, sizing guides,
            care instructions, delivery info, and FAQs to your products' own pages.
          </div>
        )}

        {enabled && (
          <div className="mt-8">
            <h2 className="text-sm font-bold uppercase tracking-wide text-ink/50">Product page details</h2>
            <p className="mt-1 text-xs text-ink/50">
              Optional — shown only on each product's own shareable page, never inside Instagram DMs.
            </p>
            <div className="mt-4 space-y-2">
              {loadingProducts ? (
                <p className="text-sm text-ink/50">Loading products…</p>
              ) : products.length === 0 ? (
                <p className="text-sm text-ink/50">Add products in your Catalog first.</p>
              ) : (
                products.map((p) => <ProductDetailsRow key={p.id} product={p} onSaved={handleSaved} />)
              )}
            </div>
          </div>
        )}
      </section>
      <DashboardFooter />
    </div>
  );
}